import styled from '@emotion/styled';
import { useFormikContext } from 'formik';
import { cloneDeep } from 'lodash';
import React, { useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { FaCheck, FaMinus, FaPencilAlt, FaPlus, FaTimes } from 'react-icons/fa';

import RxFillNumberView from 'App/components/PatientPrescription/RxFillNumberView';
import { formatDate } from 'App/components/PatientProfile/PatientProfile.utils.js';
import LimitLengthContentPopover from 'App/components/Popovers/LimitLengthContentPopover';
import TherigyInfoMessage from 'App/components/TherigyInfoMessage/TherigyInfoMessage';
import dateOrDash from 'App/utils/dateOrDash';
import useIsPermitted from 'App/utils/hooks/useIsPermitted';
import { PERMISSION_TYPES } from 'App/utils/isPermitted/isPermitted';
import pathOrDash from 'App/utils/pathOrDash';
import { CheckboxField } from 'Shared/form/CheckboxField';
import CommonPatientFieldWrapper from '../CommonPatientFieldWrapper';
import SectionTitleStyle from '../SectionTitleStyle';
import EditTherapeuticCategoryMedicationModal from './EditTherapeuticCategoryMedicationModal';
import FeatureFlag from 'App/components/FeatureFlag';
import DischargePatientModal, { dischargePatient } from '../DischargePatientModal';
import hasActiveCategories from '../DischargePatientModal/utils/hasActiveCategories';

const TherapyContainerStyle = styled.div`
    border: 1px solid #ddd;
    border-radius: 4px;
    margin-bottom: 1.25rem;

    & .btn-link {
        font-size: 12px;
        padding: 4px 6px;
    }
`;

const TherapyHeaderStyle = styled.div`
    background-color: var(--snow);
    padding: 5px 10px;
`;

const MedicationContainerStyle = styled.div`
    padding: 5px 10px;
`;

const MedicationContainerTitle = styled.div`
    font-size: 12px;
`;

const TherapeuticCategoryForm = () => {
    const { values, setFieldValue, setValues, dirty, initialValues } = useFormikContext();
    const [modalOpen, setModalOpen] = useState(false);
    const [isDischargeModalOpen, setDischargeModalOpen] = useState(false);
    const [editingTherapy, setEditingTherapy] = useState(null);
    const [editingMedication, setEditingMedication] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const isAdvancedReferralEnabled = useIsPermitted(PERMISSION_TYPES.ADVANCED_REFERRAL);

    const onCloseModal = () => {
        setModalOpen(false);
        setEditMode(false);
        setEditingTherapy(null);
        setEditingMedication(null);
    };

    return (
        <div>
            <SectionTitleStyle>
                <h2>Therapeutic Category & Medication</h2>

                <div>
                    <FeatureFlag flagName={PERMISSION_TYPES.ENABLE_FEATURE_PATIENT_DISCHARGE}>
                        <Button
                            disabled={!hasActiveCategories(values?.therapeuticCategory) && !dirty}
                            variant="link"
                            onClick={() => setDischargeModalOpen(true)}
                        >
                            <FaMinus /> Discharge Patient
                        </Button>
                    </FeatureFlag>

                    <Button variant="link" onClick={() => setModalOpen(true)}>
                        <FaPlus /> Add Therapeutic Category
                    </Button>
                </div>
            </SectionTitleStyle>

            {!values.therapeuticCategory.length && (
                <TherigyInfoMessage
                    message={
                        <>
                            Therapeutic Categories & Medications will display here. This section is{' '}
                            <strong>required</strong> in order to add a patient.
                        </>
                    }
                />
            )}

            {values.therapeuticCategory.map((therapy) => (
                <TherapyContainerStyle key={therapy.id} data-testid="therapy-container">
                    <TherapyHeaderStyle>
                        <Row>
                            <Col md={3}>
                                <div>Therapeutic Category</div>
                                <strong>{therapy.name}</strong>
                            </Col>
                            <Col md={4}>
                                <div>
                                    ICD-10 Diagnosis Code{' '}
                                    {therapy.icd10?.length ? ` (${therapy.icd10.length} Items)` : ''}
                                </div>
                                <strong>
                                    {therapy.icd10?.length ? (
                                        <LimitLengthContentPopover
                                            items={therapy.icd10.map(
                                                (icd10) => `${icd10.code} - ${icd10.name || icd10.shortDescription}`
                                            )}
                                        />
                                    ) : (
                                        '--'
                                    )}
                                </strong>
                            </Col>
                            <Col md={2}>
                                <div>Care Opt-in Status</div>
                                {therapy.status.optIn ? (
                                    <>
                                        <FaCheck className="text-success" /> Opt-in
                                        {therapy.status.start && ` - ${formatDate(therapy.status.start)}`}
                                    </>
                                ) : (
                                    <>
                                        <FaTimes className="text-danger" /> Opt-out
                                        {therapy.status.end && ` - ${formatDate(therapy.status.end)}`}
                                    </>
                                )}
                            </Col>
                            <Col md={3}>
                                <Button
                                    variant="link"
                                    className="float-right"
                                    onClick={() => {
                                        setEditingTherapy(therapy);
                                        setModalOpen(true);
                                    }}
                                >
                                    <FaPlus /> Add Medication
                                </Button>
                                <Button
                                    variant="link"
                                    className="float-right"
                                    onClick={() => {
                                        setEditingTherapy(therapy);
                                        setEditMode(true);
                                        setModalOpen(true);
                                    }}
                                >
                                    <FaPencilAlt /> Edit
                                </Button>
                                {!therapy.patientTherapyId && (
                                    <Button
                                        variant="link"
                                        className="float-right"
                                        onClick={() => {
                                            const newTherapeuticCategories = values.therapeuticCategory.filter(
                                                (tc) => tc.id !== therapy.id
                                            );
                                            setFieldValue('therapeuticCategory', newTherapeuticCategories);
                                            const newMedications = values.medications.filter(
                                                (m) => m.therapy.name !== therapy.name
                                            );
                                            setFieldValue('medications', newMedications);
                                        }}
                                    >
                                        <FaTimes /> Remove
                                    </Button>
                                )}
                            </Col>
                        </Row>
                    </TherapyHeaderStyle>
                    {values.medications
                        .filter((patientMed) => patientMed.therapy.name === therapy.name)
                        .map((patientMed) => (
                            <MedicationContainerStyle key={patientMed.medication.id} data-testid="medication-container">
                                <Row>
                                    <Col md={3}>
                                        <MedicationContainerTitle>Medication</MedicationContainerTitle>
                                        <strong>{patientMed.medication.name}</strong>
                                    </Col>
                                    <Col md={2}>
                                        <MedicationContainerTitle>Medication Start Date</MedicationContainerTitle>
                                        {dateOrDash(['status', 'start'], patientMed)}
                                    </Col>
                                    <Col md={2}>
                                        <MedicationContainerTitle>Medication End Date</MedicationContainerTitle>
                                        {dateOrDash(['status', 'end'], patientMed)}
                                    </Col>
                                    <Col md={2}>
                                        <MedicationContainerTitle>End Reason</MedicationContainerTitle>
                                        {pathOrDash(['status', 'endReason'], patientMed)}
                                    </Col>
                                    <Col md={3}>
                                        {!patientMed.id && (
                                            <Button
                                                variant="link"
                                                className="float-right"
                                                onClick={() => {
                                                    const newMedications = values.medications.filter(
                                                        (m) => m.medication.id !== patientMed.medication.id
                                                    );
                                                    setFieldValue('medications', newMedications);
                                                }}
                                            >
                                                <FaTimes /> Remove
                                            </Button>
                                        )}
                                        <Button
                                            variant="link"
                                            className="float-right"
                                            onClick={() => {
                                                setEditingTherapy(therapy);
                                                setEditingMedication(patientMed);
                                                setModalOpen(true);
                                            }}
                                        >
                                            <FaPencilAlt /> Edit
                                        </Button>
                                    </Col>
                                </Row>
                                {values.rxs
                                    .filter((rx) => rx.medication.id === patientMed.medication.id)
                                    .map((rx, index) => (
                                        <RxFillNumberView key={rx.id || `index-${index}`} prescription={rx} />
                                    ))}
                            </MedicationContainerStyle>
                        ))}
                </TherapyContainerStyle>
            ))}

            {isAdvancedReferralEnabled && (
                <CommonPatientFieldWrapper fieldName="patientReferral">
                    {(commonProps) => (
                        <CheckboxField {...commonProps} label="Receive Financial Assistance Alerts for patient" />
                    )}
                </CommonPatientFieldWrapper>
            )}

            {modalOpen && (
                <EditTherapeuticCategoryMedicationModal
                    existingTherapy={editingTherapy}
                    existingMedication={editingMedication}
                    editMode={editMode}
                    onClose={onCloseModal}
                    onSubmit={(modalValues) => {
                        const valueChanges = cloneDeep(values);
                        const existingTherapyIndex = valueChanges.therapeuticCategory.findIndex(
                            (therapy) => therapy.id === modalValues.therapy.id
                        );
                        if (existingTherapyIndex > -1) {
                            valueChanges.therapeuticCategory[existingTherapyIndex] = modalValues.therapy;
                        } else {
                            valueChanges.therapeuticCategory.push(modalValues.therapy);
                        }
                        if (modalValues.patientMedication.medication) {
                            const existingMedicationIndex = valueChanges.medications.findIndex(
                                (medication) => medication.medication.id === modalValues.patientMedication.medication.id
                            );
                            if (existingMedicationIndex > -1) {
                                valueChanges.medications[existingMedicationIndex] = modalValues.patientMedication;
                            } else {
                                valueChanges.medications.push(modalValues.patientMedication);
                            }
                        }
                        modalValues.rxs.forEach((updatedRx) => {
                            const existingRxIndex = valueChanges.rxs.findIndex((rx) =>
                                updatedRx.id
                                    ? rx.id === updatedRx.id
                                    : !rx.id &&
                                      rx.rxNumber === updatedRx.rxNumber &&
                                      rx.medication.id === updatedRx.medication.id
                            );
                            if (existingRxIndex > -1) {
                                valueChanges.rxs[existingRxIndex] = updatedRx;
                            } else {
                                valueChanges.rxs.push(updatedRx);
                            }
                        });
                        setValues(valueChanges);

                        if (modalValues.addMoreMedications) {
                            setEditingTherapy(modalValues.therapy);
                            setModalOpen(true);
                        }
                    }}
                />
            )}

            {isDischargeModalOpen ? (
                <DischargePatientModal
                    onHide={() => setDischargeModalOpen(false)}
                    onSubmit={(discharge) => {
                        const { therapeuticCategory, medications, status, deceasedDate } = dischargePatient(
                            discharge,
                            initialValues
                        );

                        return setValues({ ...values, therapeuticCategory, medications, status, deceasedDate }).then(
                            () => {
                                setDischargeModalOpen(false);
                            }
                        );
                    }}
                />
            ) : null}
        </div>
    );
};

export default TherapeuticCategoryForm;
